// /app/frontend/src/context/StepUpContext.js
/**
 * Step-Up MFA context.
 *
 * Exposes `ensureStepUp()` — a promise-based API that opens a MFA dialog if
 * needed and resolves once the user has an active step-up session. Sensitive
 * actions (delete account, wipe DB, export ZIP) call `await ensureStepUp()`
 * before hitting the backend.
 *
 * Also exposes `preferences` (persisted in Supabase user_metadata) and helpers
 * `guardIf(prefKey)` / `updatePreference()` so extra modules (Inquilinos,
 * Empleados, Finanzas edición) can be conditionally protected per user.
 */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ShieldCheck, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/context/AuthContext';

const StepUpContext = createContext(null);

export const PROTECTABLE_MODULES = [
  { key: 'protect_tenants',       label: 'Inquilinos (ver/editar)' },
  { key: 'protect_employees',     label: 'Empleados (ver/editar)' },
  { key: 'protect_finances_edit', label: 'Finanzas (crear/editar/eliminar)' },
];

const DEFAULT_PREFS = {
  protect_tenants: false,
  protect_employees: false,
  protect_finances_edit: false,
};

export const StepUpProvider = ({ children }) => {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [code, setCode] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [reason, setReason] = useState('');
  const [preferences, setPreferences] = useState(DEFAULT_PREFS);
  const resolverRef = useRef(null);

  // Load preferences from Supabase user_metadata whenever the auth user changes
  useEffect(() => {
    const raw = user?.user_metadata?.step_up_prefs;
    if (raw && typeof raw === 'object') {
      setPreferences({ ...DEFAULT_PREFS, ...raw });
    } else {
      setPreferences(DEFAULT_PREFS);
    }
  }, [user]);

  const closeDialog = useCallback((result) => {
    setOpen(false);
    setCode('');
    if (resolverRef.current) {
      resolverRef.current(result);
      resolverRef.current = null;
    }
  }, []);

  const ensureStepUp = useCallback(async (opts = {}) => {
    // Check current step-up status first
    try {
      const { data } = await api.get('/auth/step-up/status');
      if (data?.active) return true;
    } catch (_) {
      // If status fails, still show the dialog
    }
    setReason(opts.reason || 'Verifica tu identidad para continuar');
    setOpen(true);
    return new Promise((resolve) => {
      resolverRef.current = resolve;
    });
  }, []);

  const guardIf = useCallback(async (prefKey, opts = {}) => {
    if (!preferences[prefKey]) return true;
    return ensureStepUp(opts);
  }, [preferences, ensureStepUp]);

  const updatePreference = useCallback(async (key, value) => {
    const next = { ...preferences, [key]: !!value };
    // Enabling extra protection is a sensitive change -> require step-up first.
    // Disabling is also sensitive (weakens security) -> require step-up too.
    const ok = await ensureStepUp({
      reason: value
        ? 'Activar protección extra requiere verificación.'
        : 'Desactivar una protección requiere verificación.',
    });
    if (!ok) return false;
    const { error } = await supabase.auth.updateUser({
      data: { step_up_prefs: next },
    });
    if (error) {
      toast.error('No se pudo guardar la preferencia');
      return false;
    }
    setPreferences(next);
    toast.success(value ? 'Protección activada' : 'Protección desactivada');
    return true;
  }, [preferences, ensureStepUp]);

  const submit = async (e) => {
    e?.preventDefault?.();
    const cleaned = code.replace(/\D/g, '');
    if (cleaned.length !== 6) {
      toast.error('Ingresa el código de 6 dígitos');
      return;
    }
    setVerifying(true);
    try {
      await api.post('/auth/step-up/verify', { code: cleaned });
      toast.success('Identidad verificada');
      closeDialog(true);
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Código inválido o expirado';
      toast.error(detail);
    } finally {
      setVerifying(false);
    }
  };

  const cancel = () => closeDialog(false);

  const value = useMemo(
    () => ({ ensureStepUp, guardIf, preferences, updatePreference }),
    [ensureStepUp, guardIf, preferences, updatePreference]
  );

  return (
    <StepUpContext.Provider value={value}>
      {children}
      <Dialog open={open} onOpenChange={(o) => { if (!o) cancel(); }}>
        <DialogContent className="max-w-sm" data-testid="step-up-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldCheck className="w-5 h-5 text-indigo-600" />
              Verificación adicional
            </DialogTitle>
            <DialogDescription>{reason}</DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-3">
            <div>
              <Label htmlFor="step-up-code" className="text-sm">
                Código de tu app de autenticación
              </Label>
              <Input
                id="step-up-code"
                data-testid="step-up-code-input"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                autoComplete="one-time-code"
                autoFocus
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="000 000"
                className="mt-1.5 text-center text-2xl tracking-[0.4em] font-mono"
              />
              <p className="text-xs text-slate-500 mt-2">
                Ingresa el código de 6 dígitos de Google Authenticator, Authy o tu app equivalente.
              </p>
            </div>
            <DialogFooter className="gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={cancel}
                disabled={verifying}
                data-testid="step-up-cancel-btn"
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={verifying || code.length !== 6}
                className="bg-indigo-600 hover:bg-indigo-700"
                data-testid="step-up-verify-btn"
              >
                {verifying ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Verificando...</> : 'Verificar'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </StepUpContext.Provider>
  );
};

export const useStepUp = () => {
  const ctx = useContext(StepUpContext);
  if (!ctx) throw new Error('useStepUp must be used within StepUpProvider');
  return ctx;
};
